import { useState, useMemo, useCallback, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation, useSearch } from "wouter";
import { authFetch } from "@/lib/authFetch";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { isSuperAdmin } from "@shared/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Search, LayoutGrid, List, MoreHorizontal, Trash2, Eye, Mail, Phone, Building2, Users, X, GitMerge, BookUser, ClipboardPaste } from "lucide-react";
import { CopyButton } from "@/components/CopyButton";
import { Link } from "wouter";
import type { Contact } from "@shared/schema";
import { CreateContactModal } from "@/components/ContactSelector";
import { ContactSignatureImport } from "@/components/ContactSignatureImport";
import IncompleteContactsPage from "./incomplete-contacts";
import MergeLogsPage from "./merge-logs";

function avatarInitials(prenom: string, nom: string): string {
  return ((prenom?.[0] || "") + (nom?.[0] || "")).toUpperCase() || "?";
}

function avatarColor(name: string): string {
  const colors = [
    "bg-blue-500", "bg-emerald-500", "bg-violet-500", "bg-orange-500",
    "bg-pink-500", "bg-teal-500", "bg-indigo-500", "bg-amber-500",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

function orgTypeLabel(type: string | null | undefined): string {
  const map: Record<string, string> = {
    client: "Client", revendeur: "Revendeur", prospect: "Prospect",
    fournisseur: "Fournisseur", partenaire: "Partenaire", autre: "Autre",
  };
  return type ? (map[type] || type) : "";
}

function orgTypeBadgeClass(type: string | null | undefined): string {
  const map: Record<string, string> = {
    client: "border-blue-300 text-blue-700 dark:border-blue-700 dark:text-blue-400",
    revendeur: "border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-400",
    prospect: "border-purple-300 text-purple-700 dark:border-purple-700 dark:text-purple-400",
    fournisseur: "border-green-300 text-green-700 dark:border-green-700 dark:text-green-400",
    partenaire: "border-teal-300 text-teal-700 dark:border-teal-700 dark:text-teal-400",
    autre: "border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-400",
  };
  return type ? (map[type] || "") : "";
}

// ── ContactCard (grid) — composant isolé pour éviter la contamination d'état ──
function ContactCard({
  contact,
  onDelete,
}: {
  contact: any;
  onDelete: (id: string) => void;
}) {
  const [, navigate] = useLocation();
  const principal = contact.telephones?.find((t: any) => t.principal) || contact.telephones?.[0];

  return (
    <div
      className="group relative bg-card border rounded-xl p-4 hover:shadow-md hover:border-primary/30 transition-all cursor-pointer"
      data-testid={`contact-card-${contact.id}`}
      onClick={() => navigate(`/contacts/${contact.id}`)}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <Avatar className="h-10 w-10 shrink-0">
            {contact.avatarUrl && <AvatarImage src={contact.avatarUrl} />}
            <AvatarFallback className={`text-sm font-semibold text-white ${avatarColor(contact.prenom + contact.nom)}`}>
              {avatarInitials(contact.prenom, contact.nom)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="font-semibold text-sm truncate" data-testid={`text-contact-name-${contact.id}`}>
              {contact.prenom} {contact.nom}
            </p>
            {contact.poste && <p className="text-xs text-muted-foreground truncate">{contact.poste}</p>}
          </div>
        </div>

        {/* DropdownMenu hors du <Link> pour éviter les conflits de portals */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
              onClick={e => e.stopPropagation()}
              data-testid={`btn-actions-contact-${contact.id}`}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={e => {
                e.stopPropagation();
                navigate(`/contacts/${contact.id}`);
              }}
            >
              <Eye className="h-4 w-4 mr-2" />Voir la fiche
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive"
              onClick={e => {
                e.stopPropagation();
                onDelete(contact.id);
              }}
            >
              <Trash2 className="h-4 w-4 mr-2" />Supprimer
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="space-y-1.5">
        {(contact.organisationNom || contact.organisationLibre) && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Building2 className="h-3 w-3 shrink-0" />
            <span className="truncate">{contact.organisationNom || contact.organisationLibre}</span>
            {contact.organisationType && (
              <Badge variant="outline" className={`ml-auto text-[10px] h-4 px-1 shrink-0 ${orgTypeBadgeClass(contact.organisationType)}`}>
                {orgTypeLabel(contact.organisationType)}
              </Badge>
            )}
          </div>
        )}
        {contact.email && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Mail className="h-3 w-3 shrink-0" />
            <span className="truncate flex-1" data-testid={`text-contact-email-${contact.id}`}>{contact.email}</span>
            <CopyButton value={contact.email} ariaLabel="Copier l'email" size="sm" />
          </div>
        )}
        {principal && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Phone className="h-3 w-3 shrink-0" />
            <a
              href={`tel:${principal.numero}`}
              className="truncate hover:text-primary flex-1"
              onClick={e => e.stopPropagation()}
            >
              {principal.numero}
            </a>
            <CopyButton value={principal.numero} ariaLabel="Copier le numéro" size="sm" />
          </div>
        )}
      </div>
    </div>
  );
}

export default function ContactListPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const [, navigate] = useLocation();
  const search = useSearch();
  const params = new URLSearchParams(search);
  const activeTab = params.get("onglet") || "liste";

  const isSuperAdminUser = isSuperAdmin((profile as any)?.accessRole);

  const [contactSearch, setContactSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [viewMode, setViewMode] = useState<"grid" | "list">(() => {
    try { return (localStorage.getItem("contacts-view") as "grid" | "list") || "grid"; } catch { return "grid"; }
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [signatureImportOpen, setSignatureImportOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 100;
  const [incompleteCount, setIncompleteCount] = useState<number | null>(null);

  // Badge "À compléter" — fire-and-forget, jamais de crash
  useEffect(() => {
    if (!isSuperAdminUser) return;
    authFetch("/api/contacts/incomplete")
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (Array.isArray(data)) setIncompleteCount(data.length); })
      .catch(() => {});
  }, [isSuperAdminUser]);

  const { data: contactsRaw = [], isLoading } = useQuery<Contact[]>({
    queryKey: ["/api/contacts"],
    queryFn: async () => {
      const res = await authFetch("/api/contacts?limit=500");
      if (!res.ok) throw new Error("Erreur chargement contacts");
      return res.json();
    },
  });

  const { data: dupCountData } = useQuery<{ count: number }>({
    queryKey: ["/api/contacts/duplicates/count"],
    queryFn: async () => {
      const res = await authFetch("/api/contacts/duplicates/count");
      if (!res.ok) return { count: 0 };
      return res.json();
    },
    staleTime: 60_000,
  });
  const dupCount = dupCountData?.count ?? 0;

  const filtered = useMemo(() => {
    let items = contactsRaw as (Contact & { organisationNom?: string })[];
    if (filterType !== "all") items = items.filter(c => c.organisationType === filterType);
    if (contactSearch.trim()) {
      const s = contactSearch.trim().toLowerCase();
      items = items.filter(c =>
        c.prenom.toLowerCase().includes(s) ||
        c.nom.toLowerCase().includes(s) ||
        (c.email && c.email.toLowerCase().includes(s)) ||
        (c.poste && c.poste.toLowerCase().includes(s)) ||
        (c.organisationLibre && c.organisationLibre.toLowerCase().includes(s)) ||
        ((c as any).organisationNom && (c as any).organisationNom.toLowerCase().includes(s))
      );
    }
    return items;
  }, [contactsRaw, contactSearch, filterType]);

  const paginated = filtered.slice(0, page * PAGE_SIZE);
  const hasMore = filtered.length > paginated.length;

  const deleteContact = useMutation({
    mutationFn: async (id: string) => {
      const res = await authFetch(`/api/contacts/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Impossible de supprimer ce contact");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/contacts"] });
      toast({ title: "Contact supprimé" });
      setDeleteId(null);
    },
    onError: (e: any) => {
      toast({ title: "Erreur", description: e.message, variant: "destructive" });
      setDeleteId(null);
    },
  });

  const handleDeleteRequest = useCallback((id: string) => {
    setDeleteId(id);
  }, []);

  const setView = (v: "grid" | "list") => {
    setViewMode(v);
    try { localStorage.setItem("contacts-view", v); } catch {}
  };

  const clearSearch = () => {
    setContactSearch("");
    setPage(1);
  };

  const handleTabChange = (val: string) => {
    if (val === "liste") navigate("/contacts");
    else navigate(`/contacts?onglet=${val}`);
  };

  // Rediriger vers liste si onglet super-admin demandé sans droits
  useEffect(() => {
    if ((activeTab === "a-completer" || activeTab === "fusions") && profile && !isSuperAdminUser) {
      navigate("/contacts");
    }
  }, [activeTab, profile, isSuperAdminUser]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold" data-testid="text-page-title">Annuaire</h1>
          <div className="flex items-center gap-3 mt-1">
            <p className="text-muted-foreground">
              {activeTab === "liste"
                ? (isLoading ? "Chargement..." : `${filtered.length} contact${filtered.length !== 1 ? "s" : ""}`)
                : activeTab === "a-completer"
                ? "Fiches à compléter"
                : "Historique des fusions"}
            </p>
            {dupCount > 0 && activeTab === "liste" && (
              <Link href="/contacts/doublons">
                <Badge variant="outline" className="gap-1.5 cursor-pointer hover:bg-amber-50 hover:border-amber-400 border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-400 transition-colors" data-testid="badge-duplicates-count">
                  <GitMerge className="h-3 w-3" />
                  {dupCount} doublon{dupCount > 1 ? "s" : ""} à traiter
                </Badge>
              </Link>
            )}
          </div>
        </div>
        {activeTab === "liste" && (
          <div className="flex items-center gap-2">
            <div className="flex items-center border rounded-lg overflow-hidden">
              <Button variant={viewMode === "grid" ? "default" : "ghost"} size="sm" className="rounded-none" onClick={() => setView("grid")} data-testid="btn-view-grid">
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button variant={viewMode === "list" ? "default" : "ghost"} size="sm" className="rounded-none" onClick={() => setView("list")} data-testid="btn-view-list">
                <List className="h-4 w-4" />
              </Button>
            </div>
            <Button onClick={() => setCreateOpen(true)} className="shadow-lg shadow-primary/20" data-testid="button-create-contact">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau contact
            </Button>
            <Button variant="outline" onClick={() => setSignatureImportOpen(true)} data-testid="button-paste-contact">
              <ClipboardPaste className="mr-2 h-4 w-4" />
              Coller un contact
            </Button>
          </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList data-testid="tabs-contacts">
          <TabsTrigger value="liste" data-testid="tab-liste">
            <Users className="h-3.5 w-3.5 mr-1.5" />
            Contacts
          </TabsTrigger>
          {isSuperAdminUser && (
            <TabsTrigger value="a-completer" data-testid="tab-a-completer">
              <BookUser className="h-3.5 w-3.5 mr-1.5" />
              À compléter
              {incompleteCount !== null && incompleteCount > 0 && (
                <Badge className="ml-1.5 h-4 px-1.5 text-[10px] bg-amber-500 hover:bg-amber-500 text-white border-0">
                  {incompleteCount}
                </Badge>
              )}
            </TabsTrigger>
          )}
          {isSuperAdminUser && (
            <TabsTrigger value="fusions" data-testid="tab-fusions">
              <GitMerge className="h-3.5 w-3.5 mr-1.5" />
              Fusions
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="liste" className="mt-4 space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <Input
                className="pl-9 pr-9"
                placeholder="Rechercher par nom, email, société, poste..."
                value={contactSearch}
                onChange={e => { setContactSearch(e.target.value); setPage(1); }}
                data-testid="input-search-contacts"
              />
              {contactSearch.length > 0 && (
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  onClick={clearSearch}
                  data-testid="btn-clear-search"
                  tabIndex={-1}
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Select value={filterType} onValueChange={v => { setFilterType(v); setPage(1); }}>
              <SelectTrigger className="w-[180px]" data-testid="filter-type">
                <SelectValue placeholder="Type d'organisation" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                <SelectItem value="client">Clients</SelectItem>
                <SelectItem value="revendeur">Revendeurs</SelectItem>
                <SelectItem value="prospect">Prospects</SelectItem>
                <SelectItem value="fournisseur">Fournisseurs</SelectItem>
                <SelectItem value="partenaire">Partenaires</SelectItem>
                <SelectItem value="autre">Autre</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <p className="text-muted-foreground">Chargement...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 gap-3 text-muted-foreground">
              <Users className="w-10 h-10" />
              <p className="text-sm">{contactSearch || filterType !== "all" ? "Aucun résultat" : "Aucun contact pour l'instant"}</p>
              <Button variant="outline" onClick={() => setCreateOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Créer le premier contact
              </Button>
            </div>
          ) : viewMode === "grid" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {paginated.map((contact: any) => (
                <ContactCard
                  key={contact.id}
                  contact={contact}
                  onDelete={handleDeleteRequest}
                />
              ))}
            </div>
          ) : (
            <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-secondary/40 border-b">
                    <th className="text-left px-4 py-3 font-medium">Nom</th>
                    <th className="text-left px-4 py-3 font-medium hidden md:table-cell">Organisation</th>
                    <th className="text-left px-4 py-3 font-medium hidden lg:table-cell">Email</th>
                    <th className="text-left px-4 py-3 font-medium hidden xl:table-cell">Téléphone</th>
                    <th className="text-right px-4 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((contact: any, i: number) => {
                    const principal = contact.telephones?.find((t: any) => t.principal) || contact.telephones?.[0];
                    return (
                      <tr
                        key={contact.id}
                        className={`border-b hover:bg-muted/40 cursor-pointer ${i % 2 === 0 ? "" : "bg-muted/10"}`}
                        data-testid={`row-contact-${contact.id}`}
                      >
                        <td className="px-4 py-3">
                          <Link href={`/contacts/${contact.id}`} className="flex items-center gap-2.5">
                            <Avatar className="h-7 w-7 shrink-0">
                              {contact.avatarUrl && <AvatarImage src={contact.avatarUrl} />}
                              <AvatarFallback className={`text-[10px] font-semibold text-white ${avatarColor(contact.prenom + contact.nom)}`}>
                                {avatarInitials(contact.prenom, contact.nom)}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{contact.prenom} {contact.nom}</p>
                              {contact.poste && <p className="text-xs text-muted-foreground">{contact.poste}</p>}
                            </div>
                          </Link>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <div className="flex items-center gap-1.5">
                            {(contact.organisationNom || contact.organisationLibre) && (
                              <span className="text-muted-foreground text-xs">{contact.organisationNom || contact.organisationLibre}</span>
                            )}
                            {contact.organisationType && (
                              <Badge variant="outline" className={`text-[10px] h-4 px-1 ${orgTypeBadgeClass(contact.organisationType)}`}>
                                {orgTypeLabel(contact.organisationType)}
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-xs hidden lg:table-cell">{contact.email || "—"}</td>
                        <td className="px-4 py-3 text-muted-foreground text-xs hidden xl:table-cell">
                          {principal ? <a href={`tel:${principal.numero}`} className="hover:text-primary">{principal.numero}</a> : "—"}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Link href={`/contacts/${contact.id}`}>
                              <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`btn-view-contact-${contact.id}`}>
                                <Eye className="h-3.5 w-3.5" />
                              </Button>
                            </Link>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-destructive hover:text-destructive"
                              onClick={() => handleDeleteRequest(contact.id)}
                              data-testid={`btn-delete-contact-${contact.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {hasMore && (
            <div className="flex justify-center pt-2">
              <Button variant="outline" onClick={() => setPage(p => p + 1)} data-testid="btn-load-more">
                Afficher plus ({filtered.length - paginated.length} restants)
              </Button>
            </div>
          )}
        </TabsContent>

        {isSuperAdminUser && (
          <TabsContent value="a-completer" className="mt-4">
            <IncompleteContactsPage />
          </TabsContent>
        )}

        {isSuperAdminUser && (
          <TabsContent value="fusions" className="mt-4">
            <MergeLogsPage />
          </TabsContent>
        )}
      </Tabs>

      <CreateContactModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          queryClient.invalidateQueries({ queryKey: ["/api/contacts"] });
          setCreateOpen(false);
        }}
      />
      <ContactSignatureImport
        open={signatureImportOpen}
        onClose={() => setSignatureImportOpen(false)}
        onCreated={() => {
          queryClient.invalidateQueries({ queryKey: ["/api/contacts"] });
          setSignatureImportOpen(false);
        }}
      />

      <AlertDialog open={!!deleteId} onOpenChange={v => { if (!v) setDeleteId(null); }}>
        <AlertDialogContent onCloseAutoFocus={e => e.preventDefault()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce contact ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le contact sera retiré de toutes les tâches associées.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => deleteId && deleteContact.mutate(deleteId)}
              data-testid="btn-confirm-delete-contact"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
